import apiClient from './api';
import { API_ENDPOINTS } from '@/config/api.config';
import {
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ChangePasswordRequest,
  PasswordStrengthResponse,
  ApiResponse,
  ERROR_CODES
} from '@/types/auth.types';

class PasswordService {
  
  // Request password reset email
  async forgotPassword(email: string, requestId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
      }>>(API_ENDPOINTS.PASSWORD.FORGOT, {
        email,
        request_id: requestId
      } as ForgotPasswordRequest);
      
      // Always return success to prevent email enumeration
      return {
        success: true,
        message: response.data.data?.message || 'If an account exists with this email, password reset instructions have been sent.'
      };
    } catch (error: any) {
      console.error('Forgot password error:', error);
      
      // Still return success to prevent email enumeration
      return {
        success: true,
        message: 'If an account exists with this email, password reset instructions have been sent.'
      };
    }
  }
  
  // Validate password reset token
  async validateResetToken(token: string): Promise<{ valid: boolean; email?: string; expiresIn?: number }> {
    try {
      const response = await apiClient.get<ApiResponse<{
        valid: boolean;
        email: string;
        expires_in: number;
        message: string;
      }>>(API_ENDPOINTS.PASSWORD.VALIDATE_TOKEN, {
        params: { token }
      });
      
      if (response.data.success && response.data.data) {
        return {
          valid: response.data.data.valid,
          email: response.data.data.email,
          expiresIn: response.data.data.expires_in
        };
      }
      
      return { valid: false };
    } catch (error: any) {
      console.error('Token validation error:', error);
      
      // Check for specific error codes
      const errorCode = error.response?.data?.error?.error;
      if (errorCode === 'TOKEN_EXPIRED') {
        throw new Error('This password reset link has expired. Please request a new one.');
      }
      
      return { valid: false };
    }
  }
  
  // Reset password with token
  async resetPassword(token: string, encryptedNewPassword: string, encryptedConfirmPassword: string, requestId: string): Promise<{ success: boolean; message: string; redirectUrl?: string }> {
    try {
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
        redirect_url?: string;
      }>>(API_ENDPOINTS.PASSWORD.RESET, {
        token,
        new_password: encryptedNewPassword,
        confirm_password: encryptedConfirmPassword,
        request_id: requestId
      } as ResetPasswordRequest);
      
      if (response.data.success && response.data.data) {
        return {
          success: response.data.data.success,
          message: response.data.data.message,
          redirectUrl: response.data.data.redirect_url
        };
      }
      
      throw new Error('Password reset failed');
    } catch (error: any) {
      console.error('Password reset error:', error);
      
      // Map error codes to user-friendly messages
      const errorCode = error.response?.data?.error?.error;
      const errorMessage = ERROR_CODES[errorCode as keyof typeof ERROR_CODES] || 
                          error.response?.data?.error?.error_description || 
                          error.response?.data?.description ||
                          error.message ||
                          'Password reset failed';
      
      throw new Error(errorMessage);
    }
  }
  
  // Change password for authenticated user
  async changePassword(currentPassword: string, newPassword: string, confirmPassword: string): Promise<{ success: boolean; message: string; requiresRelogin: boolean }> {
    try {
      // Validate passwords match
      if (newPassword !== confirmPassword) {
        throw new Error(ERROR_CODES.PASSWORDS_DO_NOT_MATCH);
      }
      
      // Check if new password is same as current
      if (currentPassword === newPassword) {
        throw new Error(ERROR_CODES.SAME_PASSWORD);
      }
      
      // Check password strength first
      const strength = await this.checkPasswordStrength(newPassword);
      if (strength.strength === 'weak') {
        throw new Error('Password is too weak. Please choose a stronger password.');
      }
      
      const response = await apiClient.post<ApiResponse<{
        success: boolean;
        message: string;
        requires_relogin: boolean;
      }>>(API_ENDPOINTS.PASSWORD.CHANGE, {
        current_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword
      } as ChangePasswordRequest);
      
      if (response.data.success && response.data.data) {
        return {
          success: response.data.data.success,
          message: response.data.data.message,
          requiresRelogin: response.data.data.requires_relogin || false
        };
      }
      
      throw new Error('Password change failed');
    } catch (error: any) {
      console.error('Password change error:', error);
      
      // Map error codes to user-friendly messages
      const errorCode = error.response?.data?.error?.error;
      const errorMessage = ERROR_CODES[errorCode as keyof typeof ERROR_CODES] || 
                          error.response?.data?.error?.error_description || 
                          error.response?.data?.description ||
                          error.message ||
                          'Password change failed';
      
      throw new Error(errorMessage);
    }
  }
  
  // Check password strength
  async checkPasswordStrength(password: string): Promise<{
    strength: 'weak' | 'medium' | 'strong' | 'very_strong';
    score: number;
    requirements: {
      min_length: boolean;
      has_uppercase: boolean;
      has_lowercase: boolean;
      has_number: boolean;
      has_special: boolean;
    };
    suggestions: string[];
  }> {
    try {
      const response = await apiClient.post<PasswordStrengthResponse>(API_ENDPOINTS.PASSWORD.CHECK_STRENGTH, {
        password
      });
      
      if (response.data.success && response.data.data) {
        // Ensure suggestions array exists
        return {
          ...response.data.data,
          suggestions: response.data.data.suggestions || []
        };
      }
      
      // Fallback to client-side validation
      return this.checkPasswordStrengthLocal(password);
    } catch (error) {
      // If API fails, use client-side validation
      return this.checkPasswordStrengthLocal(password);
    }
  }
  
  // Local password strength check (fallback)
  private checkPasswordStrengthLocal(password: string): {
    strength: 'weak' | 'medium' | 'strong' | 'very_strong';
    score: number;
    requirements: {
      min_length: boolean;
      has_uppercase: boolean;
      has_lowercase: boolean;
      has_number: boolean;
      has_special: boolean;
    };
    suggestions: string[];
  } {
    const requirements = {
      min_length: password.length >= 8,
      has_uppercase: /[A-Z]/.test(password),
      has_lowercase: /[a-z]/.test(password),
      has_number: /\d/.test(password),
      has_special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    let score = 0;
    let metRequirements = 0;
    
    if (requirements.min_length) {
      score += 20;
      metRequirements++;
    }
    if (requirements.has_uppercase) {
      score += 20;
      metRequirements++;
    }
    if (requirements.has_lowercase) {
      score += 20;
      metRequirements++;
    }
    if (requirements.has_number) {
      score += 20;
      metRequirements++;
    }
    if (requirements.has_special) {
      score += 20;
      metRequirements++;
    }
    
    // Bonus for length
    if (password.length >= 12) score += 10;
    if (password.length >= 16) score += 10;
    
    // Cap at 100
    score = Math.min(score, 100);
    
    let strength: 'weak' | 'medium' | 'strong' | 'very_strong';
    if (score < 40) strength = 'weak';
    else if (score < 60) strength = 'medium';
    else if (score < 80) strength = 'strong';
    else strength = 'very_strong';
    
    const suggestions: string[] = [];
    if (!requirements.min_length) suggestions.push('Use at least 8 characters');
    if (!requirements.has_uppercase) suggestions.push('Include uppercase letters');
    if (!requirements.has_lowercase) suggestions.push('Include lowercase letters');
    if (!requirements.has_number) suggestions.push('Include numbers');
    if (!requirements.has_special) suggestions.push('Include special characters');
    if (password.length < 12) suggestions.push('Consider using a longer password');
    
    return {
      strength,
      score,
      requirements,
      suggestions
    };
  }
  
  // Generate a strong password suggestion
  generatePasswordSuggestion(): string {
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const numbers = '0123456789';
    const special = '!@#$%^&*()';
    const all = uppercase + lowercase + numbers + special;
    
    let password = '';
    
    // Ensure at least one of each required character type
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest randomly
    for (let i = password.length; i < 12; i++) {
      password += all[Math.floor(Math.random() * all.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => 0.5 - Math.random()).join('');
  }
  
  // Validate password requirements
  validatePasswordRequirements(password: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    return {
      isValid: errors.length === 0,
      errors
    };
  }
}

export const passwordService = new PasswordService();
export default passwordService;